import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import { ChartConfig, ChartContainer, ChartLegend, ChartTooltip, ChartTooltipContent } from '@bpinternal/ui-kit-next'
import { type FC } from 'react'
import { formatters } from '../../../utils'
import { FlattenedDailySummary } from '../utils'

const chartConfig = {
  dailyAverage: {
    label: 'Average AI Spend',
    color: 'hsl(var(--border))',
  },
  projectedCumulative: {
    label: 'Projected AI Spend',
    color: 'hsl(var(--border))',
  },
} satisfies ChartConfig

type SpendType = 'total' | 'daily'
type Props = {
  data?: FlattenedDailySummary[]
  selectedMonth: Date
  activeChart: SpendType
  visibleKeys: string[]
  title?: string
}

const generateMonthTicks = (selectedMonth: Date) => {
  const year = selectedMonth.getFullYear()
  const month = selectedMonth.getMonth()
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const ticks = []
  for (let day = 1; day <= daysInMonth; day++) {
    ticks.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`)
  }

  return ticks
}

const nameFormatter = (name: string) => {
  if (name === 'projectedCumulative') {
    return 'Projected AI Spend'
  }
  if (name === 'dailyAverage') {
    return 'Average AI Spend'
  }
  if (name.endsWith('_value') || name.endsWith('_cumulative')) {
    return name.replace(/_(value|cumulative)$/, '')
  }
  return name
}

export const DailySpendChart: FC<Props> = ({ data, selectedMonth, activeChart, visibleKeys, title }) => {
  return (
    <>
      {title && <h2>{title}</h2>}
      <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
        <AreaChart
          accessibilityLayer
          data={data}
          margin={{
            left: 12,
            right: 12,
          }}
        >
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tickFormatter={(value) => value.slice(8, 10)}
            ticks={generateMonthTicks(selectedMonth)}
          />
          <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => formatters.nanodollars(value)} />
          <ChartTooltip
            cursor={false}
            filterNull={true}
            content={
              <ChartTooltipContent
                valueFormatter={(value) => {
                  return formatters.nanodollars(value as number)
                }}
                nameFormatter={(name) => {
                  if (typeof name === 'number') return name.toString()
                  return nameFormatter(name)
                }}
              />
            }
          />
          <defs>
            {Array.from({ length: 6 }).map((_, i) => (
              <linearGradient key={`gradient-${i}`} id={`fillGradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={`hsl(var(--chart-${i + 1}))`} stopOpacity={0.8} />
                <stop offset="95%" stopColor={`hsl(var(--chart-${i + 1}))`} stopOpacity={0.1} />
              </linearGradient>
            ))}
          </defs>
          <Area
            dataKey={activeChart === 'total' ? 'projectedCumulative' : 'dailyAverage'}
            type="monotone"
            fill="none"
            stroke={`var(--color-${activeChart === 'total' ? 'projectedCumulative' : 'dailyAverage'})`}
            strokeWidth={2}
            strokeDasharray="5 5"
            connectNulls={true}
            stackId="b"
          />
          {visibleKeys.map((key, i) => {
            return (
              <Area
                key={`${key}-${i}`}
                dataKey={activeChart === 'total' ? `${key}_cumulative` : `${key}_value`}
                stackId="1"
                stroke={`hsl(var(--chart-${i + 1}))`}
                fill={`url(#fillGradient-${i})`}
                fillOpacity={0.4}
                type="bump"
              />
            )
          })}
          <ChartLegend
            wrapperStyle={
              {
                '--color-projectedCumulative': 'hsl(var(--muted-foreground))',
                '--color-dailyAverage': 'hsl(var(--muted-foreground))',
              } as React.CSSProperties
            }
            formatter={(value) => nameFormatter(value as string)}
          />
        </AreaChart>
      </ChartContainer>
    </>
  )
}
